import api from "services/api"
import { issueToken, getAuthToken, parseToken } from "utils/tokenManager"
import logger from "utils/logger"

const TENANT_STORAGE_KEY = "tenantId"

const getTenantIdFromAuthPayload = authData => {
  if (authData?.accountScope === "super_admin") {
    return null
  }
  return (
    authData?.tenantId ||
    authData?.tenant?._id ||
    authData?.tenant?.id ||
    authData?.user?.tenantId ||
    authData?.profile?.tenantId ||
    (authData?.token ? parseToken(authData.token)?.tenantId : null) ||
    null
  )
}

/**
 * Authenticate with email and password.
 *
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} The API response payload.
 */
export const login = async (email, password) => {
  const response = await api.post("auth/login", { email, password })
  const data = response.data
  if (data?.status && data?.data) {
    issueToken(data.data)
    localStorage.setItem("accountScope", data?.data?.accountScope || "tenant")
    const responseTenantId = getTenantIdFromAuthPayload(data.data)
    if (responseTenantId) {
      localStorage.setItem(TENANT_STORAGE_KEY, responseTenantId)
      localStorage.setItem("TENANT_ID", responseTenantId)
    } else if (data?.data?.accountScope === "super_admin") {
      localStorage.removeItem(TENANT_STORAGE_KEY)
      localStorage.removeItem("TENANT_ID")
    }
  }
  return data
}

/**
 * Log out the current user and clear all stored tokens.
 *
 * @returns {Promise<void>}
 */
export const logout = async () => {
  try {
    await api.post("auth/logout")
  } catch (err) {
    logger.warn("Logout API call failed, clearing tokens anyway", err)
  }
}

/**
 * Request a new access token using the stored refresh token.
 *
 * @returns {Promise<string>} The new access token.
 */
export const refreshToken = async () => {
  const storedRefresh = localStorage.getItem("refreshToken")
  if (!storedRefresh) throw new Error("No refresh token stored")
  const response = await api.post("auth/refresh-token", {
    refreshToken: storedRefresh,
  })
  const data = response.data
  const tokenPayload = data?.data?.token ? data.data : data
  if (data?.status && tokenPayload?.token) {
    issueToken(tokenPayload)
    localStorage.setItem("accountScope", tokenPayload?.accountScope || "tenant")
    const responseTenantId = getTenantIdFromAuthPayload(tokenPayload)
    if (responseTenantId) {
      localStorage.setItem(TENANT_STORAGE_KEY, responseTenantId)
      localStorage.setItem("TENANT_ID", responseTenantId)
    } else if (tokenPayload?.accountScope === "super_admin") {
      localStorage.removeItem(TENANT_STORAGE_KEY)
      localStorage.removeItem("TENANT_ID")
    }
    return tokenPayload.token
  }
  throw new Error(data?.msg || "Token refresh failed")
}

/**
 * Validate the current access token against the backend.
 *
 * @returns {Promise<boolean>}
 */
export const validateToken = async () => {
  const token = getAuthToken()
  if (!token) return false
  try {
    const response = await api.post("auth/validate-token", { token })
    return !!(response.data?.status)
  } catch (err) {
    logger.error("Token validation error", err)
    return false
  }
}

/**
 * Return the current access token from localStorage.
 *
 * @returns {string|null}
 */
export const getAuthTokenValue = () => getAuthToken()

/**
 * Manually set (overwrite) the access token in localStorage.
 *
 * @param {string} token
 */
export const setAuthToken = token => {
  if (!token) return
  issueToken({ token })
}
